import {
  Overlay,
  OverlayRef,
  ScrollStrategyOptions,
} from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  TemplateRef,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';

type MenuPlacement = 'left' | 'right';

/** Aside menu component */
@Component({
  selector: 'atx-aside-menu',
  templateUrl: './aside-menu.component.html',
  styleUrls: ['./aside-menu.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class AsideMenuComponent implements AfterViewInit, OnChanges, OnDestroy {
  private asideMenuOverlayRef: OverlayRef | null;

  @Input()
  public open = false;

  @Input()
  public smallerWidthOnMobile = false;

  @Input()
  public showCloseButton = false;

  /** Set to true to use via cdk overlay */
  @Input()
  public useOverlay = true;

  /** Menu placement position */
  @Input()
  public placement: MenuPlacement = 'left';

  @Input()
  public rootClass: string;

  @Input()
  public backdropClass: string;

  @Input()
  public disableBackdropClick = false;

  @Input()
  public disposeOnNavigation = false;

  /** Open state change emitter */
  @Output()
  public openStateChange: EventEmitter<boolean> = new EventEmitter();

  public get isPlacementLeft() {
    return this.placement === 'left';
  }

  public get isPlacementRight() {
    return this.placement === 'right';
  }

  @ViewChild('templatePortalContent')
  public templatePortalContent: TemplateRef<unknown>;

  public attached = false;

  private templatePortal: TemplatePortal;
  private destroy$ = new Subject<void>();

  constructor(
    private overlay: Overlay,
    private viewContainerRef: ViewContainerRef,
    private cdr: ChangeDetectorRef,
    private scrollStrategyOptions: ScrollStrategyOptions,
  ) {}

  public ngOnChanges(changes: SimpleChanges) {
    if (
      changes.open &&
      !changes.open.firstChange &&
      changes.open.currentValue !== changes.open.previousValue
    ) {
      this.setMenuOpen(this.open);
    }
  }

  public ngAfterViewInit() {
    this.templatePortal = new TemplatePortal(
      this.templatePortalContent,
      this.viewContainerRef,
    );
  }

  public ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();

    if (this.asideMenuOverlayRef) {
      this.asideMenuOverlayRef.dispose();
      this.asideMenuOverlayRef = null;
    }
  }

  public toggleOpen() {
    this.setMenuOpen(!this.open);
  }

  public setMenuOpen(open: boolean) {
    this.open = open;
    this.openStateChange.emit(this.open);
    if (this.useOverlay && this.templatePortal) {
      if (open) {
        this.attachOverlay();
      } else {
        this.detachOverlay();
      }
    }
  }

  private attachOverlay() {
    if (this.asideMenuOverlayRef) {
      this.asideMenuOverlayRef.detach();
    }
    this.asideMenuOverlayRef =
      this.asideMenuOverlayRef ??
      this.overlay.create({
        hasBackdrop: true,
        backdropClass: this.backdropClass,
        height: '100%',
        disposeOnNavigation: this.useOverlay ? this.disposeOnNavigation : false,
        scrollStrategy: this.scrollStrategyOptions.block(),
      });
    this.asideMenuOverlayRef.attach(this.templatePortal);
    this.asideMenuOverlayRef
      .backdropClick()
      .pipe(
        filter(() => !this.disableBackdropClick),
        takeUntil(this.destroy$),
      )
      .subscribe(() => this.setMenuOpen(false));
    this.setAttached(true);
    this.updateMenuSide();
  }

  private detachOverlay() {
    if (this.asideMenuOverlayRef) {
      this.asideMenuOverlayRef.detach();
    }
    this.setAttached(false);
  }

  private setAttached(value: boolean) {
    window.setTimeout(() => {
      this.attached = value;
      this.cdr.detectChanges();
    });
  }

  private updateMenuSide() {
    if (!this.asideMenuOverlayRef) {
      return;
    }

    const styleRef = this.asideMenuOverlayRef.overlayElement.style;
    if (this.isPlacementRight) {
      styleRef.left = 'auto';
      styleRef.right = '0';
    } else {
      styleRef.left = '0';
      styleRef.right = 'auto';
    }
  }
}
